import {
  Info, AlertTriangle, AlertOctagon, CheckCircle2, type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * LEVEL 4 — a surface that means something.
 *
 * The point of flattening the layout is that the boxes which remain get to
 * carry information again. This is the one place a tinted, bordered
 * rectangle is still the right answer: the reader has to notice it, and the
 * intensity has to be proportional to what it says.
 *
 * The ladder, deliberately not uniform:
 *
 *   neutral    no border, no tint — it is just prose that sits apart
 *   attention  faint tint, no border — "worth knowing", not "act now"
 *   warning    tinted + bordered — something is wrong and you must act
 *   blocking   tinted + bordered + heavier — work cannot proceed
 *   success    the quietest of all — a finished thing does not need a
 *              full-width green panel to keep congratulating the reader
 *
 * Status is never colour alone: every tone ships an icon, and the icon is
 * `aria-hidden` because `role` + the text carry the meaning for a reader
 * that cannot see the tint.
 */
export type StatusPanelTone = "neutral" | "attention" | "warning" | "blocking" | "success";

const TONE: Record<StatusPanelTone, { cls: string; icon: LucideIcon; iconCls: string }> = {
  neutral: {
    cls: "bg-transparent",
    icon: Info,
    iconCls: "text-fg-muted",
  },
  attention: {
    cls: "bg-bg-raised/60 rounded-lg",
    icon: Info,
    iconCls: "text-status-info",
  },
  warning: {
    cls: "rounded-lg border border-status-warning/30 bg-status-warning/[0.07]",
    icon: AlertTriangle,
    iconCls: "text-status-warning",
  },
  blocking: {
    cls: "rounded-lg border border-status-critical/35 bg-status-critical/[0.08]",
    icon: AlertOctagon,
    iconCls: "text-status-critical",
  },
  success: {
    cls: "bg-transparent",
    icon: CheckCircle2,
    iconCls: "text-status-ok",
  },
};

export function StatusPanel({
  tone = "neutral",
  title,
  children,
  action,
  icon,
  className,
  "data-testid": testId,
}: {
  tone?: StatusPanelTone;
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** One control, right-aligned — the thing that resolves the status. */
  action?: React.ReactNode;
  /** Override the tone's icon where the domain has a better one. */
  icon?: LucideIcon;
  className?: string;
  "data-testid"?: string;
}) {
  const t = TONE[tone];
  const Icon = icon ?? t.icon;
  // A warning or a blocker interrupts; the quieter tones are just part of
  // the page and must not shove themselves into the reader's ear.
  const live = tone === "warning" || tone === "blocking";
  return (
    <div
      data-testid={testId}
      data-tone={tone}
      role={live ? "alert" : undefined}
      className={cn(
        "flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3",
        // Untinted tones get no padding box at all — they are prose.
        tone === "neutral" || tone === "success" ? "py-0.5" : "px-3 py-2.5",
        t.cls,
        className,
      )}
    >
      <Icon size={14} aria-hidden className={cn("shrink-0 mt-0.5", t.iconCls)} />
      <div className="min-w-0 flex-1 space-y-1">
        {title && <div className="text-[12.5px] font-medium text-fg-bright">{title}</div>}
        {children && <div className="text-[12.5px] leading-relaxed text-fg-dim">{children}</div>}
      </div>
      {action && <div className="shrink-0 self-start sm:self-center">{action}</div>}
    </div>
  );
}
